// Modal de aplicação/edição de desconto — Story 3.8.
// Preview do preço efetivo em tempo real via helper shared.

import * as Dialog from '@radix-ui/react-dialog';
import { Tag, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { calculateEffectivePrice, todayLocal, type DiscountType } from '@control-center/shared';

import { cn } from '@control-center/ui';

import { Field, Input, Textarea } from '../clients/form-primitives';
import { PillButton } from '../clients/PillButton';
import { useApplyDiscount } from '../../hooks/use-subscriptions';
import { extractErrorMessage } from '../../lib/errors';
import type { SubscriptionDetail } from '../../lib/subscriptions-api';

interface DiscountFormProps {
  subscription: SubscriptionDetail | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function formatBRL(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function DiscountForm({
  subscription,
  open,
  onOpenChange,
}: DiscountFormProps): JSX.Element | null {
  // Pré-preenche com valores atuais se sub já tem desconto (modo editar)
  const [discountType, setDiscountType] = useState<DiscountType>('percent');
  const [discountAmount, setDiscountAmount] = useState<string>('');
  const [discountReason, setDiscountReason] = useState<string>('');
  const [noExpiry, setNoExpiry] = useState<boolean>(true);
  const [discountUntil, setDiscountUntil] = useState<string>('');
  const applyMutation = useApplyDiscount();

  useEffect(() => {
    if (open && subscription) {
      // Modo editar: pré-preenche
      if (subscription.discountAmount && subscription.discountType) {
        setDiscountType(subscription.discountType);
        setDiscountAmount(subscription.discountAmount);
        setDiscountReason(subscription.discountReason ?? '');
        if (subscription.discountUntil) {
          setNoExpiry(false);
          setDiscountUntil(subscription.discountUntil);
        } else {
          setNoExpiry(true);
          setDiscountUntil('');
        }
      } else {
        // Modo aplicar (novo)
        setDiscountType('percent');
        setDiscountAmount('');
        setDiscountReason('');
        setNoExpiry(true);
        setDiscountUntil('');
      }
      applyMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, subscription]);

  if (!subscription) return null;

  const basePrice = Number(subscription.price ?? '0');
  const amountNum = Number(discountAmount);
  const isAmountValid =
    Number.isFinite(amountNum) &&
    amountNum > 0 &&
    (discountType === 'percent' ? amountNum <= 100 : amountNum < basePrice);

  // Preview do effective_price em tempo real
  const previewEffective = calculateEffectivePrice({
    price: basePrice,
    discountAmount: isAmountValid ? amountNum : null,
    discountType: isAmountValid ? discountType : null,
    discountUntil: !noExpiry && discountUntil ? discountUntil : null,
  });

  const reasonValid = discountReason.length <= 500;
  const untilValid = noExpiry || (discountUntil && discountUntil > todayLocal());
  const canSubmit = isAmountValid && reasonValid && untilValid && !applyMutation.isPending;

  async function handleConfirm(): Promise<void> {
    if (!canSubmit || !subscription) return;
    try {
      await applyMutation.mutateAsync({
        id: subscription.id,
        payload: {
          discount_amount: amountNum,
          discount_type: discountType,
          discount_reason: discountReason.trim() || undefined,
          discount_until: noExpiry ? undefined : discountUntil,
        },
      });
      toast.success('Desconto aplicado.', {
        description: `Preço efetivo: ${formatBRL(previewEffective)}/mês`,
      });
      onOpenChange(false);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Falha ao aplicar desconto.'));
    }
  }

  const isEditing = Boolean(subscription.discountAmount);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          )}
        />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
            'w-[min(560px,calc(100vw-32px))] max-h-[90dvh] overflow-y-auto',
            'rounded-[14px] border border-line bg-panel p-6 shadow-lg focus:outline-none',
          )}
        >
          <div className="mb-4 flex items-start justify-between gap-4">
            <div>
              <Dialog.Title className="flex items-center gap-2 text-[18px] font-semibold tracking-[-0.005em] text-ink">
                <Tag size={16} strokeWidth={1.8} className="text-warn" />
                {isEditing ? 'Editar desconto' : 'Aplicar desconto'}
              </Dialog.Title>
              <p className="mt-1 text-[12px] text-muted">
                {subscription.client.name} · Plano <strong>{subscription.plan?.name ?? '—'}</strong>{' '}
                ({formatBRL(basePrice)}/mês)
              </p>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Fechar"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-panel-2 hover:text-ink"
              >
                <X size={15} strokeWidth={2} />
              </button>
            </Dialog.Close>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              void handleConfirm();
            }}
            className="flex flex-col gap-4"
          >
            <Field label="Tipo de desconto" required>
              {() => (
                <div className="flex items-center gap-2">
                  <label
                    className={cn(
                      'flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-[8px] border px-3 py-2 text-[13px]',
                      discountType === 'percent'
                        ? 'border-warn bg-warn/10 text-ink'
                        : 'border-line bg-panel-2 text-muted hover:bg-panel-3',
                    )}
                  >
                    <input
                      type="radio"
                      name="discount_type"
                      value="percent"
                      checked={discountType === 'percent'}
                      onChange={() => setDiscountType('percent')}
                      className="sr-only"
                    />
                    Percentual (%)
                  </label>
                  <label
                    className={cn(
                      'flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-[8px] border px-3 py-2 text-[13px]',
                      discountType === 'fixed'
                        ? 'border-warn bg-warn/10 text-ink'
                        : 'border-line bg-panel-2 text-muted hover:bg-panel-3',
                    )}
                  >
                    <input
                      type="radio"
                      name="discount_type"
                      value="fixed"
                      checked={discountType === 'fixed'}
                      onChange={() => setDiscountType('fixed')}
                      className="sr-only"
                    />
                    Valor fixo (R$)
                  </label>
                </div>
              )}
            </Field>

            <Field
              label={
                discountType === 'percent' ? 'Percentual de desconto' : 'Valor de desconto (R$)'
              }
              required
              hint={
                discountType === 'percent' ? 'Entre 0 e 100' : `Menor que ${formatBRL(basePrice)}`
              }
              error={
                discountAmount.length > 0 && !isAmountValid
                  ? discountType === 'percent'
                    ? 'Percentual deve estar entre 0 e 100.'
                    : `Valor deve ser maior que zero e menor que ${formatBRL(basePrice)}.`
                  : undefined
              }
            >
              {({ inputId }) => (
                <Input
                  id={inputId}
                  type="number"
                  step={discountType === 'percent' ? '1' : '0.01'}
                  min="0.01"
                  max={discountType === 'percent' ? '100' : String(basePrice - 0.01)}
                  tabular
                  value={discountAmount}
                  onChange={(e) => setDiscountAmount(e.target.value)}
                />
              )}
            </Field>

            <Field label="Validade">
              {() => (
                <div className="flex flex-col gap-2">
                  <label className="flex items-center gap-2 text-[12px] text-muted">
                    <input
                      type="checkbox"
                      checked={noExpiry}
                      onChange={(e) => {
                        setNoExpiry(e.target.checked);
                        if (e.target.checked) setDiscountUntil('');
                      }}
                      className="h-4 w-4 rounded border-line"
                    />
                    Sem prazo (perpétuo)
                  </label>
                  {!noExpiry && (
                    <Input
                      type="date"
                      min={todayLocal()}
                      value={discountUntil}
                      onChange={(e) => setDiscountUntil(e.target.value)}
                      hasError={Boolean(discountUntil && discountUntil <= todayLocal())}
                    />
                  )}
                </div>
              )}
            </Field>

            <Field
              label="Motivo (opcional)"
              hint="Max 500 caracteres — auditoria"
              error={discountReason.length > 500 ? 'Máximo 500 caracteres.' : undefined}
            >
              {({ inputId }) => (
                <Textarea
                  id={inputId}
                  rows={2}
                  maxLength={500}
                  value={discountReason}
                  onChange={(e) => setDiscountReason(e.target.value)}
                  placeholder="Ex: Negociação retenção / cliente fiel"
                />
              )}
            </Field>

            {/* Preview do preço efetivo em tempo real */}
            {isAmountValid && (
              <div className="rounded-[12px] border border-line bg-panel-2 p-4">
                <div className="mb-2 text-[11px] uppercase tracking-wider text-muted-3">
                  Preview do preço efetivo
                </div>
                <div className="flex items-baseline gap-3">
                  <span className="text-[15px] text-muted line-through tnum">
                    {formatBRL(basePrice)}
                  </span>
                  <span className="text-[20px] font-semibold text-good tnum">
                    {formatBRL(previewEffective)}/mês
                  </span>
                </div>
                <p className="mt-2 text-[11px] text-muted">
                  Aplicado automaticamente na próxima geração de cobrança (cron diário).
                </p>
              </div>
            )}

            <div className="mt-2 flex items-center justify-end gap-2">
              <PillButton type="button" onClick={() => onOpenChange(false)}>
                Cancelar
              </PillButton>
              <PillButton type="submit" solid disabled={!canSubmit}>
                {applyMutation.isPending
                  ? 'Aplicando…'
                  : isEditing
                    ? 'Atualizar desconto'
                    : 'Aplicar desconto'}
              </PillButton>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
